import React from "react"
import { Typography, dayjs } from "@carrotfertility/carotene-core"
import {
  FormCheckbox,
  FormCheckboxGroup,
  FormDatePicker,
  FormRadioGroup,
  FormSelect,
  FormTextField
} from "@carrotfertility/carotene-core-x"
import { Markdown } from "@internal/ui"

export interface CustomModule {
  id: string
  label: string | null
  sublabel: string | null
  hipaa_name: string | null
  mod_type: string
  options: string | null
  options_array: string[] | null
  required: boolean | null
  position: number | null
  parent_custom_module_id: string | null
}

// Prefer Healthie's structured `options_array`; fall back to newline-delimited `options`.
export function parseOptions(mod: CustomModule): Array<{ label: string; value: string }> {
  const raw = mod.options_array ?? (mod.options ?? "").split(/\r?\n/)
  return raw
    .map((s) => s.trim())
    .filter(Boolean)
    .map((o) => ({ label: o, value: o }))
}

export const DISPLAY_ONLY_TYPES = new Set(["read_only", "label"])
export const UNSUPPORTED_TYPES = new Set([
  "signature",
  "matrix",
  "medications",
  "synced_allergy",
  "intolerance/sensitivity"
])
const DATE_TYPES = new Set(["date", "date_picker", "dob"])

export const isMultiSelect = (mod: CustomModule): boolean => mod.mod_type === "checkbox"
export const isBoolean = (mod: CustomModule): boolean => mod.mod_type === "agree_to_above"
export const isDate = (mod: CustomModule): boolean => DATE_TYPES.has(mod.mod_type)
export const isInput = (mod: CustomModule): boolean =>
  !DISPLAY_ONLY_TYPES.has(mod.mod_type) && !UNSUPPORTED_TYPES.has(mod.mod_type)

// Prefixed so a numeric Healthie id isn't read as an array index in the RHF field path.
export const fieldName = (mod: CustomModule): string => `q_${mod.id}`

// Date controls hold a dayjs object, not a string.
export type FieldValue = string | string[] | boolean | dayjs.Dayjs | null

export function toDefaultValue(mod: CustomModule, answer: string): FieldValue {
  if (isMultiSelect(mod)) return answer.split(/\r?\n/).filter(Boolean)
  if (isBoolean(mod)) return answer === "true" || answer === "1"
  if (isDate(mod)) return answer ? dayjs(answer) : null
  return answer
}

export function toHealthieAnswer(mod: CustomModule, value: unknown): string {
  if (isMultiSelect(mod)) return Array.isArray(value) ? value.join("\n") : ""
  if (isBoolean(mod)) return value === true ? "true" : ""
  if (isDate(mod)) return dayjs.isDayjs(value) && value.isValid() ? value.format("YYYY-MM-DD") : ""
  return typeof value === "string" ? value : ""
}

// Healthie's rich-text editor emits spacer paragraphs (`<p>&nbsp;</p>`) that stack on top
// of the shared markdown <p> spacing into oversized gaps. Strip them so the renderer alone
// controls spacing — a local workaround rather than forking @internal/ui's base elements
// (shared by ~15 host consumers) with a telemedicine-specific spacing scale. No-op for
// non-HTML copy (no <p> tags to match).
const EMPTY_PARAGRAPH_RE = /<p\b[^>]*>(?:\s|&nbsp;|&#160;|&#xa0;|<span\b[^>]*>|<\/span>|<br\s*\/?>)*<\/p>/gi
function stripSpacerParagraphs(html: string): string {
  return html.replace(EMPTY_PARAGRAPH_RE, "")
}

// Renders one Healthie question as the matching carotene-core-x control, keyed off `mod_type`.
export function FormField({ mod, name: nameOverride }: { mod: CustomModule; name?: string }): JSX.Element | null {
  const label = mod.label || mod.hipaa_name || "(untitled)"
  const name = nameOverride ?? fieldName(mod)
  const required = mod.required ?? false
  // react-hook-form uses the `required` rule value as the error message, so pass the
  // copy (not a bare boolean) — otherwise the field errors with no "Required" text.
  const requiredRule = required ? "Required" : false
  const helperText = mod.sublabel ?? undefined
  const options = parseOptions(mod)

  const textField = (type: string, extra?: Record<string, unknown>): JSX.Element => (
    <FormTextField
      name={name}
      label={label}
      helperText={helperText}
      required={required}
      registerOptions={{ required: requiredRule }}
      fullWidth
      type={type}
      {...extra}
    />
  )
  const select = (): JSX.Element => (
    <FormSelect
      name={name}
      label={label}
      helperText={helperText}
      required={required}
      controllerProps={{ rules: { required: requiredRule } }}
      fullWidth
      options={options}
    />
  )

  const displayText = (mod.label ?? "").trim()
  // read_only copy lives in `options` as rich-text HTML (label is a placeholder); fall back to label.
  const readOnlyContent = stripSpacerParagraphs(mod.options ?? "").trim() || displayText

  if (UNSUPPORTED_TYPES.has(mod.mod_type)) {
    // eslint-disable-next-line no-console -- dev signal; unsupported types are dropped, not shown
    console.warn(`[telemedicine] unsupported Healthie mod_type "${mod.mod_type}" — not rendered`)
    return null
  }

  switch (mod.mod_type) {
    case "read_only":
      return readOnlyContent ? <Markdown>{readOnlyContent}</Markdown> : null
    case "label":
      return displayText ? (
        <Typography variant="subtitle1" sx={{ fontWeight: (theme) => theme.typography.fontWeightBold }}>
          <Markdown inline>{displayText}</Markdown>
        </Typography>
      ) : null

    case "textarea":
      return (
        <FormTextField
          name={name}
          label={label}
          helperText={helperText}
          required={required}
          registerOptions={{ required: requiredRule }}
          fullWidth
          multiline
          minRows={3}
        />
      )
    case "date":
    case "date_picker":
    case "dob":
      return (
        <FormDatePicker
          name={name}
          label={label}
          helperText={helperText}
          required={required}
          controllerProps={{ rules: { required: requiredRule } }}
        />
      )
    case "email":
      return textField("email")
    case "phone":
      return textField("tel")
    case "number":
      return textField("number")

    case "dropdown":
      return select()
    case "radio":
    case "horizontal_radio":
      return (
        <FormRadioGroup
          name={name}
          label={label}
          helperText={helperText}
          required={required}
          controllerProps={{ rules: { required: requiredRule } }}
          options={options}
        />
      )
    case "checkbox":
      return (
        <FormCheckboxGroup
          name={name}
          label={label}
          helperText={helperText}
          required={required}
          controllerProps={{ rules: { required: requiredRule } }}
          options={options}
        />
      )
    case "agree_to_above":
      // FormControlLabel shows no asterisk, so append one when required.
      return (
        <FormCheckbox
          name={name}
          label={
            <>
              <Markdown inline>{label}</Markdown>
              {required ? " *" : ""}
            </>
          }
          controllerProps={{ defaultValue: false, rules: { required: requiredRule } }}
        />
      )

    case "text":
    case "name":
    default:
      // Unknown type carrying options is almost always a select (gender, pronouns).
      if (options.length > 0) return select()
      return textField("text")
  }
}
